import { Box, Stack } from "@mui/material";
import { CurrencyBalancesSheet } from "Components/Dashboard/BalanceWithPaymentActions/MobileBalance/Components/CurrencyBalancesSheet";
import { CurrencyPill } from "Components/Dashboard/BalanceWithPaymentActions/MobileBalance/Components/CurrencyPill";
import type { BalancesProps } from "Components/Dashboard/BalanceWithPaymentActions/types";
import { Balance } from "Components/Shared/Balance";
import { useState } from "react";

/**
 * The mobile hero balance: ONE currency at a time. Stacking every currency pushed the hero past
 * the screen once EUR accounts arrived, so the phone shows the chosen currency and a pill that
 * names it and opens the switcher. The pill only exists when there is more than one currency;
 * the choice itself is owned by BalanceContainer (persisted per persona, shared with desktop).
 */
export const MobileCurrencyBalance = ({
  balances,
  currencies,
  selectedCurrency,
  selectCurrency,
  isBalanceBlurred,
  visibilityToggle,
}: BalancesProps) => {
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const shown =
    balances.find(({ currency }) => currency === selectedCurrency) ??
    balances[0];
  const hasCurrencyChoice = currencies.length > 1;

  return (
    <Stack alignItems="center" gap={3}>
      {/* The eye belongs to the amount line; the pill below centres on the whole stack. */}
      <Box display="flex" alignItems="center" gap={2}>
        <Box
          sx={{
            filter: isBalanceBlurred ? "blur(8px)" : "none",
            pointerEvents: isBalanceBlurred ? "none" : "auto",
            userSelect: isBalanceBlurred ? "none" : "auto",
            transition: "filter 0.15s ease",
          }}
        >
          <Balance
            balance={shown?.total ?? 0}
            currency={shown?.currency}
            colorVariant="light"
          />
        </Box>
        {visibilityToggle}
      </Box>
      {hasCurrencyChoice && selectedCurrency && (
        <>
          <CurrencyPill
            currency={selectedCurrency}
            currencyCount={currencies.length}
            onClick={() => setIsSheetOpen(true)}
          />
          <CurrencyBalancesSheet
            isOpen={isSheetOpen}
            onClose={() => setIsSheetOpen(false)}
            balances={balances}
            selectedCurrency={selectedCurrency}
            isBalanceBlurred={isBalanceBlurred}
            onSelect={selectCurrency}
          />
        </>
      )}
    </Stack>
  );
};
